// @atlas/contracts — a `contacts` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { AccountPersonRefSchema } from '../_shared/schemas.js';
/**
 * Egy fiók-kötődés a kapcsolattartó-sorban: melyik account(hoz) tartozik a kontakt és milyen minőségben.
 * A kontakt (egy identity) TÖBB fiókhoz is kötődhet — ezért a sor egy `accounts` tömböt hordoz.
 */
export const ContactAffiliationSchema = z.object({
    accountId: z.string(),
    /** A fiók neve (a foundation `accounts`-ból feloldva); ismeretlen fiók esetén null. */
    accountName: z.string().nullable(),
    /** Elsődleges kapcsolattartó ezen a fiókon (a legacy `is_primary`). */
    isPrimary: z.boolean(),
    /** Van-e ügyfél-portál hozzáférése ezen a kötődésen (a legacy `portal_access`). */
    portalAccess: z.boolean(),
    /* A per-fiók partner SZEREP (`portal-admin|member|viewer`) SZÁNDÉKOSAN NINCS ezen a soron:
       a kötődés-erőforrás nem hordozza, a BFF-ben `'admin'` placeholder volt, és a lista minden során ugyanazt
       mutatta. A szerep a fiók oldalán él, és ott is állítható — `modules/admin/portal-access.md`. */
});
/**
 * Kapcsolattartó-sor: egy identity (type=partner/contact), aki `account_affiliations`-on át `kind='contact'`
 * minőségben egy vagy több fiókhoz kötődik. A név/e-mail a foundation `identities`-ből, a fiók-kötődések
 * (account + is_primary + portal_access) az `account-affiliations` resource-busz sorokból dúsítva, identityre
 * aggregálva. A `portalAccess`/`isPrimary` sor-szint: bármely kötődésen igaz → a jelző igaz (van hozzáférés).
 */
export const ContactRowSchema = z.object({
    /** A kapcsolattartó identity-je (a sor kulcsa). */
    id: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    /** OIDC név-részek — a locale-tudatos megjelenítéshez (HU: vezetéknév elöl). A `displayName` a fallback. */
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** Presigned avatar-URL (SeaweedFS, S3-kompatibilis objektum-tár), ha van profilkép — különben null (monogram fallback). */
    avatarUrl: z.string().nullable(),
    /** Telefonszám (a `platform.identity_profiles.phone`-ból) — az Elérhetőségek oszlophoz. */
    phone: z.string().nullable(),
    /** Beosztás (a `platform.identity_profiles.job_title`-ből) — a Kapcsolattartó-oszlop név alatti sora. */
    position: z.string().nullable(),
    /**
     * Becenév (a `platform.identity_profiles.nickname`-ből) — a szerkesztő előtöltéséhez (a create/update
     * round-trip ne veszítsen becenevet). NOTE: az Odin `listIdentities` read-projekciója JELENLEG nem adja
     * vissza (csak phone/jobTitle) → amíg ez nem bővül, `null` marad; a szerkesztő ilyenkor dirty-alapú
     * kihagyással véd (nem nulláz némán meglévő értéket).
     */
    nickname: z.string().nullable(),
    /** A kontakt identity-státusza (aktív/felfüggesztve/archivált) — a dedikált utolsó oszlophoz. */
    status: z.string(),
    /**
     * KIVEZETVE — ISO időbélyeg, vagy `null` az élő soron.
     *
     * A „törlés" a kapcsolattartónál sosem fizikai törlés volt: a kötődések és a portál-hozzáférés megszűnik,
     * az identity pedig archiválódik (soft delete). A felület EZ ALAPJÁN kapuz — kivezetett soron nincs
     * szerkesztés és nincs újra-kivezetés, viszont van **visszavétel**.
     */
    offboardedAt: z.string().nullable().default(null),
    /** A fiók-kötődések (melyik account(ok)hoz, milyen minőségben). */
    affiliations: z.array(ContactAffiliationSchema),
    /** Elsődleges kapcsolattartó legalább egy fiókon. */
    isPrimary: z.boolean(),
    /** Van portál-hozzáférése legalább egy kötődésen. */
    portalAccess: z.boolean(),
    /** A meghívó (üdvözlő levél) utolsó kiadása — ISO, vagy `null` = soha nem kapott (Odin `identities.invited_at`). */
    invitedAt: z.string().nullable().default(null),
    /**
     * KÍNÁLHATÓ-E az üdvözlő levél (user-döntés) — a SZERVER dönti el: élő, e-mailes sor · legalább egy kötött fiókján
     * VALÓDI portál-hozzáférés (Odin grant, nem a leíró `portalAccess` bit) · és még NEM kapott meghívót. A levél
     * egyszeri; elveszett vagy lejárt linknél a belépőn az „Elfelejtett jelszó" ad újat.
     */
    welcomeAvailable: z.boolean().default(false),
    /**
     * ÜGYFÉLADMINISZTRÁTOROK: a kontakt fiókjaihoz kötött BELSŐ kollégák (`account_affiliations` `kind='admin'`),
     * identity-re **egyediesítve** (egy admin több közös fiókon is ülhet → egyszer szerepel) és névre rendezve.
     *
     * A legacy „Kapcsolattartók" lista 4. oszlopa (`UserQueryService::getContacts` `myAccounts.admins`) — ez a
     * kontakt-lista egyetlen olyan adata, ami NEM a kontakt sajátja, hanem a fiókjain KERESZTÜL jön: a kérdés,
     * amire válaszol, hogy „kihez tartozik nálunk ez a kapcsolattartó". A legacy szűrni is tudott rá — az Atlasban
     * az EGYSÉGES kereső fedi (a `q` az admin-nevekre is illeszkedik).
     */
    admins: z.array(AccountPersonRefSchema),
});
/** Kapcsolattartó-lista lekérdezés: név/e-mail keresés + név szerinti rendezés (magyar collation — csak az
 *  irány váltható) + offset-lapozás a végtelen görgetéshez (a lista áttekinthető méretű → gyakorlatban egy lap). */
export const ContactListQuerySchema = z.object({
    q: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    /** Rendezés/megjelenítés nyelve: `hu` → vezetéknév elöl, `en` → keresztnév elöl (mint a Munkatársak-listán). */
    lang: z.enum(['hu', 'en']).optional(),
    /**
     * ÉLETCIKLUS-SZŰRŐ — `active` (ALAP) · `archived` (kivezetettek) · `all`.
     *
     * ⚠️ KORÁBBAN ez a nyers identity-státusz-enumra ment (`active|suspended|archived`), és a háromból KETTŐ
     * sosem talált semmit: `suspended` sor nem létezik (nem is állítható be sehol), az `archived` pedig
     * egyben soft-deletelt, amit az upstream vissza sem adott. A szűrő mostantól az ÉLETCIKLUSRA megy.
     */
    status: z.enum(['active', 'archived', 'all']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/**
 * Az „Új kapcsolattartó" write-body (partner-provisioning): az identity-mag (e-mail KÖTELEZŐ, kulcs) + a
 * partner-profil-mezők (telefon/beosztás/becenév). Az Odin identity-create `type:"partner"` — a szerver küldi,
 * NEM a kliens (a body-ban NINCS `type`).
 *
 * FIÓK-KÖTÉS ITT NINCS (user-döntés): a kapcsolattartót a FIÓK oldalán, a „Kapcsolattartók" szekcióban lehet
 * fiókhoz rendelni — ott dől el a portál-hozzáférés, a szerep és az elsődleges jelölés is. Egy kapcsolattartó
 * akár húsz fiókhoz is tartozhat; az egy-fiókos választó itt félrevezető volt.
 */
export const ContactCreateBodySchema = z.object({
    email: z.string().trim().email().max(320),
    // a NÉV mindkét része KÖTELEZŐ (user-döntés) — üres stringet sem fogadunk el
    givenName: z.string().trim().min(1).max(255),
    familyName: z.string().trim().min(1).max(255),
    displayName: z.string().trim().max(255).nullable().optional(),
    phone: z.string().trim().max(64).nullable().optional(),
    jobTitle: z.string().trim().max(255).nullable().optional(),
    nickname: z.string().trim().max(255).nullable().optional(),
});
/**
 * A „Kapcsolattartó szerkesztése" write-body: az identity-mag + partner-profil PARCIÁLIS PATCH-e (e-mail/nevek/
 * telefon/beosztás/becenév — üres string → mező-törlés az Odin oldalán). A `type` NEM szerkeszthető (nincs a
 * body-ban). Fiók-kötés itt sincs: a kötés-flag-ek a fiók oldalán szerkeszthetők.
 */
export const ContactUpdateBodySchema = z.object({
    email: z.string().trim().email().max(320).optional(),
    // a NÉV szerkesztéskor sem üríthető (user-döntés): ha jön, nem üres — `null` (törlés) sincs
    givenName: z.string().trim().min(1).max(255).optional(),
    familyName: z.string().trim().min(1).max(255).optional(),
    displayName: z.string().trim().max(255).nullable().optional(),
    phone: z.string().trim().max(64).nullable().optional(),
    jobTitle: z.string().trim().max(255).nullable().optional(),
    nickname: z.string().trim().max(255).nullable().optional(),
});
//# sourceMappingURL=schemas.js.map